import { useEffect, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import './browser-source.css';
import './widget.css';
import { useWidgetChat, type LiveChatMessage } from './widgets/use-widget-chat.js';
import { WidgetRender } from './widgets/WidgetRender.js';

/**
 * The page an OBS or Meld Browser Source points at (ARCHITECTURE.md §103.6).
 *
 * ==================== WHAT THIS PAGE IS ====================
 * A renderer, and nothing else. It holds one opaque token, asks the server
 * what to draw, and draws it. It computes no totals: goal progress is
 * server-authoritative (§103.7), because the same URL may be open in OBS, in
 * Meld, in a preview and in a browser tab at once, and four accumulators
 * would put four different numbers on four screens.
 * ===========================================================
 *
 * ==================== UNTRUSTED CONTENT ====================
 * Display names and provider-formatted amounts are provider-supplied text.
 * Every one of them is rendered as a React text child and never through
 * `dangerouslySetInnerHTML`. A viewer whose display name is markup appears on
 * stream as characters, not as HTML. There is no configuration field that
 * accepts markup, CSS or script in Slice 1 (§103.9), so there is nothing here
 * that could be asked to interpret one.
 * ===========================================================
 */

/** Matches the class `browser-source.css` scopes its `body` rules to. */
const OVERLAY_BODY_CLASS = 'connaro-overlay-active';

/** The same fact the API states in `WidgetTokenError`, told the same way. */
const INVALID_TOKEN_MESSAGE = 'This widget link is not valid.';
const MISSING_TOKEN_MESSAGE = 'This widget link is missing its token.';

/** How long before re-reading state, absent a realtime nudge. */
const POLL_MS = 5_000;

type Bootstrap = {
  widgetType: string;
  enabled: boolean;
  config: Record<string, unknown> | null;
  state: Record<string, unknown> | null;
};

export function WidgetRuntimePage(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('k');

  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [error, setError] = useState<string | null>(
    token === null || token === '' ? MISSING_TOKEN_MESSAGE : null,
  );

  /*
   * Applied before paint, and removed on unmount. See `browser-source.css`
   * for why an unscoped `body` selector cannot be trusted to win the cascade,
   * and would leak onto every Console route besides.
   */
  useLayoutEffect(() => {
    document.body.classList.add(OVERLAY_BODY_CLASS);
    return () => {
      document.body.classList.remove(OVERLAY_BODY_CLASS);
    };
  }, []);

  useEffect(() => {
    if (token === null || token === '') return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    /*
     * Stop for good, not just this pass.
     *
     * An earlier version returned from `read()` on a 401 and left the interval
     * running, so the source kept asking every five seconds for as long as the
     * scene stayed open — the exact behaviour the 401 exists to prevent. The
     * component test asserted only that the message appeared, which it did, so
     * nothing failed. A real browser against the real deployment is what
     * showed the second request.
     */
    const stop = (): void => {
      if (timer !== undefined) clearInterval(timer);
      timer = undefined;
    };

    const read = async (): Promise<void> => {
      try {
        const response = await fetch(
          `/api/v1/widget-runtime/bootstrap?k=${encodeURIComponent(token)}`,
          { headers: { accept: 'application/json' } },
        );

        if (cancelled) return;

        /*
         * 401 is terminal. The API returns it for an unknown, revoked or
         * deleted widget — none of which a retry fixes — so this stops rather
         * than polling a dead token for as long as the scene stays open.
         */
        if (response.status === 401) {
          setError(INVALID_TOKEN_MESSAGE);
          stop();
          return;
        }

        if (!response.ok) return;

        setBootstrap((await response.json()) as Bootstrap);
        setError(null);
      } catch {
        // A transient network failure keeps the last frame on screen rather
        // than blanking a live broadcast.
      }
    };

    void read();
    timer = setInterval(() => {
      void read();
    }, POLL_MS);

    return () => {
      cancelled = true;
      stop();
    };
  }, [token]);

  if (error !== null) {
    return <p className="widget__notice">{error}</p>;
  }

  // Nothing is drawn until the server has said what to draw. A half-rendered
  // widget flashing on a scene change is worse than one that appears a beat late.
  if (bootstrap === null) return <div className="widget" />;

  /*
   * A DISABLED widget draws nothing at all — not an error, not a placeholder.
   * The operator switched it off; the scene should look as though the source
   * were not there, and it starts drawing again by itself when they switch it
   * back on.
   */
  if (!bootstrap.enabled) return <div className="widget" />;

  return <LiveWidget bootstrap={bootstrap} token={token} />;
}

/**
 * The drawn widget, once the server has said what to draw.
 *
 * A separate component ONLY so the chat socket's hooks can run
 * unconditionally. The parent returns early for the loading, disabled and
 * invalid-token states, and a hook called after those returns would break the
 * rules of hooks the first time a widget was disabled mid-broadcast.
 */
function LiveWidget({
  bootstrap,
  token,
}: {
  bootstrap: Bootstrap;
  token: string | null;
}): React.JSX.Element {
  const isChat = bootstrap.widgetType === 'CHAT_OVERLAY';
  const config = bootstrap.config;
  const state = bootstrap.state ?? {};

  /*
   * The replay array's IDENTITY is what re-seeds the stream, so it must not be
   * rebuilt on every render — a new `[]` each time would clear the overlay on
   * every poll. `state.messages` is a stable reference for as long as the
   * bootstrap object is.
   */
  const replay = (state.messages as LiveChatMessage[] | undefined) ?? EMPTY_REPLAY;

  const messages = useWidgetChat({
    token,
    replay,
    maxMessages: typeof config?.maxMessages === 'number' ? config.maxMessages : 15,
    lifetimeSeconds:
      typeof config?.messageLifetimeSeconds === 'number' ? config.messageLifetimeSeconds : 0,
    active: isChat,
  });

  return (
    <WidgetRender
      widgetType={bootstrap.widgetType}
      config={config}
      state={isChat ? { ...state, messages } : state}
      token={token}
    />
  );
}

/** One stable empty array, for the same reason `replay` is not rebuilt inline. */
const EMPTY_REPLAY: LiveChatMessage[] = [];


